import * as React from 'react';
import { css } from '@emotion/react';
import { useRifm } from 'rifm';
import { FieldBase, FieldBaseProps } from '../FieldBase';
import { InputBase } from '../InputBase';
import { InputBaseProps } from '../InputBase/InputBase';

export type DateFieldTheme = {
    widthShortDate: number;
    widthFullDate: number;
};

export const dateFieldTheme: DateFieldTheme = {
    widthShortDate: 90,
    widthFullDate: 138,
};

function makeMaskFromFormat(format: string, maskChar: string) {
    return format.replace(/[a-z]/gi, maskChar);
}

const maskedDateFormatter = (mask: string, numberMaskChar: string) => (value: string) => {
    const refuse = /[^\d]+/gi;
    let result = '';
    const parsed = value.replace(refuse, '');

    if (parsed === '') {
        return parsed;
    }

    let i = 0;
    let n = 0;
    while (i < mask.length) {
        const maskChar = mask[i];
        if (maskChar === numberMaskChar && n < parsed.length) {
            const parsedChar = parsed[n];
            result += parsedChar;
            n += 1;
        } else {
            result += maskChar;
        }
        i += 1;
    }

    return result;
};

export interface DateFieldProps
    extends Pick<FieldBaseProps, 'label' | 'fullWidth' | 'validationStatus'>,
        InputBaseProps {
    /**
     * A reference passed to the `input` element.
     */
    inputRef?: React.Ref<HTMLInputElement>;

    /**
     * The text which will be used as the `input` element's label.
     */
    label?: string;

    /**
     * If `true`, the day component of the date will not be captured.
     */
    suppressDay?: boolean;

    /**
     * If `true`, the year component of the date will be two digits in length instead of four.
     */
    twoDigitYear?: boolean;

    /**
     * The value of the input element.
     */
    value: string;

    /**
     * Callback fired when the value of the `input` element is changed.
     * @param {React.ChangeEvent} event The event source of the callback.
     */
    onChange?: (event: React.ChangeEvent<HTMLInputElement> | string) => void;

    /**
     * If `true`, formatted value will be passed on change.
     * @default false
     */
    isOnChangeFormattingRequired?: boolean;

    /**
     * Ref to be passed to each cloned child.
     */
    ref?: React.Ref<any>;
}

/**
 * DateField component allows for display and entering of dates in multiple formats. It has built-in validation states
 * to reflect the date entered by the user. Additionally, it can be customised to cater and support for multiple date formats such as Two digit years, Four digit years,
 * exclusion of date. All these features make this component extensible and reusable for multiple scenarios.
 */
/**
 * @deprecated
 * This component has been superseded by DateField2.
 */
export function DateField(props: Readonly<DateFieldProps>): React.JSX.Element {
    const {
        disabled,
        error,
        fullWidth,
        id,
        inputRef,
        label,
        // onChangeFormatted,
        onChange,
        required,
        twoDigitYear,
        suppressDay,
        value,
        isOnChangeFormattingRequired = false,
        validationStatus,
        ref,
        ...other
    } = props;

    const format = `${suppressDay ? '' : 'DD/'}MM/${twoDigitYear ? 'YY' : 'YYYY'}`;
    const maskChar = '_';
    const inputMask = makeMaskFromFormat(format, maskChar);
    const formatter = React.useMemo(() => maskedDateFormatter(inputMask, maskChar), [inputMask, maskChar]);

    const rifm = useRifm({
        value,
        onChange: isOnChangeFormattingRequired ? (onChange as (value: string) => void) : (_: string) => null,
        format: formatter,
    });

    const getInputWidth = (): string => {
        if (fullWidth) {
            return '100%';
        }
        const width = suppressDay && twoDigitYear ? dateFieldTheme.widthShortDate : dateFieldTheme.widthFullDate;
        return `${width}px`;
    };

    const inputStyles = css`
        width: ${getInputWidth()};
    `;

    return (
        <FieldBase
            ref={ref}
            id={id}
            label={label}
            disabled={disabled}
            fullWidth={fullWidth}
            validationStatus={validationStatus}
        >
            <InputBase
                ref={inputRef}
                css={inputStyles}
                placeholder={format}
                id={id}
                value={rifm.value}
                onChange={
                    isOnChangeFormattingRequired
                        ? rifm.onChange
                        : (e: React.ChangeEvent<HTMLInputElement>) => {
                              rifm.onChange(e);
                              if (onChange) {
                                  onChange(e);
                              }
                          }
                }
                disabled={disabled}
                error={error}
                required={required}
                type="tel"
                {...other}
            />
        </FieldBase>
    );
}
